(() => {
  "use strict";

  window.zag = window.zag || {};
  window.Weglot = window.Weglot || {};

  window.zag.components.ZagLocalization = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      this.initializeLanguageCodeInput();

      this.events.add(this.section, "click", (event) => {
        if (event.target == this.section) {
          this.hide();
        }
      });
      this.events.add(this.closeButton, "click", this.hide);

      this.events.add(this.countrySelector, "change", this.onCountryChange);
      this.languageList.forEach((language) => {
        this.events.add(language, "change", this.onLanguageChange);
      });
      this.events.add(this.form, "submit", this.onFormSubmit);
    }

    async initializeLanguageCodeInput() {
      await this.waitForWeglot();

      if (this.languageCodeInput) {
        this.languageCodeInput.value = this.currentLanguageCode;
      }
    }

    waitForWeglot() {
      return new Promise((resolve) => {
        if (window.Weglot && window.Weglot.getCurrentLang) {
          resolve();
        } else {
          const checkWeglot = setInterval(() => {
            if (window.Weglot && window.Weglot.getCurrentLang) {
              clearInterval(checkWeglot);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkWeglot);
            resolve();
          }, 5000);
        }
      });
    }

    get weglotContainer() {
      return this.cache.get("dom", "weglotContainer", () => {
        return document.querySelector(".weglot-container");
      });
    }

    get popinContainer() {
      return this.cache.get("dom", "popinContainer", () => {
        return this.querySelector(".popin-container");
      });
    }

    get closeButton() {
      return this.cache.get("dom", "closeButton", () => {
        return this.querySelector(".popin-close");
      });
    }

    get form() {
      return this.cache.get("dom", "form", () => {
        return this.querySelector("form");
      });
    }

    get currentLanguageCode() {
      return window.Weglot?.getCurrentLang?.() || "fr";
    }

    get currentCountryCode() {
      return window.Shopify?.country || "FR";
    }

    get returnToInput() {
      return this.cache.get("dom", "returnTo", () => {
        return this.querySelector('input[name="return_to"]');
      });
    }

    get languageCodeInput() {
      return this.cache.get("dom", "languageCode", () => {
        return this.querySelector('input[name="language_code"]');
      });
    }

    get countryCodeInput() {
      return this.cache.get("dom", "countryCode", () => {
        return this.querySelector('input[name="country_code"]');
      });
    }

    get countrySelector() {
      return this.cache.get("dom", "countrySelector", () => {
        return this.querySelector(".country-selector select");
      });
    }

    get flagList() {
      return this.cache.get("dom", "flagList", () => {
        return Array.from(this.querySelectorAll(".country-flag"));
      });
    }

    get languageList() {
      return this.cache.get("dom", "languageList", () => {
        return Array.from(this.querySelectorAll(".language-container"));
      });
    }

    onCountryChange = () => {
      const newCountryCode = this.countrySelector.value;

      this.countryCodeInput.value = newCountryCode;

      this.flagList.forEach((flag) => {
        flag.getAttribute("value") === newCountryCode
          ? flag.setAttribute("data-selected", "")
          : flag.removeAttribute("data-selected");
      });
    };

    onLanguageChange = (event) => {
      const newLanguageCode = event.currentTarget.getAttribute("data-language");

      this.languageCodeInput.value = newLanguageCode;

      this.languageList.forEach((language) => {
        const checkbox = language.querySelector('input[type="checkbox"]');
        checkbox.checked =
          language.getAttribute("data-language") === newLanguageCode;
      });
    };

    onFormSubmit = async (event) => {
      event.preventDefault();

      const languageCode = this.languageCodeInput.value;
      const countryCode = this.countryCodeInput.value;

      const changedCountry = this.currentCountryCode !== countryCode;
      const changedLanguage = this.currentLanguageCode !== languageCode;

      if (!changedLanguage && !changedCountry) {
        this.hide();
        return;
      }

      let cleanPathname = this.getCleanPathname();
      const newSubfolder = this.buildSubfolder(languageCode, countryCode);

      let returnUrl =
        newSubfolder +
        cleanPathname +
        window.location.search +
        window.location.hash;

      this.returnToInput.value = returnUrl;

      try {
        if (changedCountry) {
          await this.submitShopifyFormWithoutRedirect();
        }
      } catch (error) {
        console.error("Localization switch failed:", error);
      } finally {
        this.hide();
        window.location.href = returnUrl;
      }
    };

    submitShopifyFormWithoutRedirect = async () => {
      const formData = new FormData(this.form);

      try {
        const response = await fetch(this.form.action || "/localization", {
          method: "POST",
          body: formData,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
      } catch (error) {
        console.error("Failed to submit localization form:", error);
        throw error;
      }
    };

    getCleanPathname = () => {
      const pathname = window.location.pathname;

      if (
        this.currentLanguageCode === "fr" &&
        this.currentCountryCode === "FR"
      ) {
        return pathname;
      }

      if (
        this.currentCountryCode === "FR" &&
        this.currentLanguageCode !== "fr"
      ) {
        const languagePrefix = new RegExp(
          `^/${this.currentLanguageCode}(?:/|$)`
        );
        const cleanPath = pathname.replace(languagePrefix, "/");

        return cleanPath;
      }

      const currentSubfolder = `/${
        this.currentLanguageCode
      }-${this.currentCountryCode.toLowerCase()}/`;
      if (pathname.startsWith(currentSubfolder)) {
        return pathname.replace(currentSubfolder, "/");
      }

      return pathname;
    };

    buildSubfolder = (languageCode, countryCode) => {
      if (languageCode === "fr" && countryCode === "FR") {
        return "";
      }

      if (countryCode === "FR" && languageCode !== "fr") {
        return `/${languageCode}`;
      }

      return `/${languageCode}-${countryCode.toLowerCase()}`;
    };

    updateActiveLanguage = () => {
      this.languageList.forEach((language) => {
        const checkbox = language.querySelector('input[type="checkbox"]');
        checkbox.checked =
          language.getAttribute("data-language") === this.currentLanguageCode;
      });
    };

    show = async () => {
      await this.initializeLanguageCodeInput();

      this.updateActiveLanguage();

      this.modifiers.add("popin-open");
      window.zag.utils.documentScroll.isLocked = true;
    };

    hide = (event) => {
      this.modifiers.remove("popin-open");
      window.zag.utils.documentScroll.isLocked = false;
    };
  };
  if (!window.customElements.get("zag-localization")) {
    window.customElements.define(
      "zag-localization",
      window.zag.components.ZagLocalization
    );
  }
})();
